import { throttle } from 'lodash-es';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import { deleteLocalSession, getLocalSessions } from 'services/nim';
import { scheduleIdle } from 'utils/scheduleIdle';
import lastUsedConversation from 'utils/lastUsedConversation';
import { useCallback } from 'react';
import { batchGetTalk } from 'services/batchRequest/batchGetTalk';

type ConversationId = Conversation.Conversation['conversationId'];
type ConversationType = Conversation.Conversation;
type ConversationSummary = Conversation.ConversationSummary;

// ============================================================================
// 配置
// ============================================================================

// 默认最大回收时间 1 小时
const MAX_RECYCLE_TIME = 1000 * 60 * 60;

// 默认最大会话数量
let maxConversationsLimit = 3000;

// 设置最大会话数量限制
export const setMaxConversationsLimit = (limit: number) => {
  maxConversationsLimit = limit;
};

// 获取当前最大会话数量限制
export const getMaxConversationsLimit = () => maxConversationsLimit;

// ============================================================================
// 新架构：基于 atomFamily 的会话状态管理
// ============================================================================

// 中央 Map 存储所有会话数据
const conversationMapAtom = atom<Map<ConversationId, ConversationType>>(new Map());

// 会话 ID 列表（用于列表渲染，与 Map 分离以优化渲染）
const conversationIdListAtom = atom<ConversationId[]>([]);

// ============================================================================
// ConversationSummary Map - 用于排序和过滤，不进行 LRU 回收
// ============================================================================

// 会话摘要 Map：只存储排序和过滤所需的字段，不进行回收
const conversationSummaryMapAtom = atom<Map<ConversationId, ConversationSummary>>(new Map());

// 活跃会话 ID 集合（当前正在查看的会话，防止被回收）
const activeConversationIdsAtom = atom<Set<ConversationId>>(new Set<ConversationId>());

// 视口会话 ID 集合（当前视口及附近的会话，防止被回收）
const viewportConversationIdsAtom = atom<Set<ConversationId>>(new Set<ConversationId>());

// 正在加载中的会话 ID 集合（防止重复请求）
const loadingConversationIds = new Set<ConversationId>();

// atomFamily：按 conversationId 获取单个会话的 atom
export const conversationAtomFamily = atomFamily((conversationId: ConversationId) =>
  atom(
    (get) => get(conversationMapAtom).get(conversationId),
    (get, set, next: ConversationType) => {
      const map = new Map(get(conversationMapAtom));
      map.set(conversationId, next);
      set(conversationMapAtom, map);
    },
  ),
);

// ============================================================================
// LRU 回收机制
// ============================================================================

// 设置 atomFamily 的回收条件：不在最近使用列表中的会话可被回收
conversationAtomFamily.setShouldRemove((createdAt, conversationId: ConversationId) => {
  const doNotRemove = lastUsedConversation.data.includes(conversationId);
  if (doNotRemove) {
    return false;
  }
  return Date.now() - createdAt > MAX_RECYCLE_TIME;
});

// ============================================================================
// NIM 会话清理（保留原有逻辑）
// ============================================================================

const clearNimSessions = throttle(() => {
  scheduleIdle(() => {
    getLocalSessions((sessions) => {
      deleteLocalSession(sessions.map((s) => s.id));
    });
  });
}, 5000);

// ============================================================================
// 派生状态
// ============================================================================

// 获取所有会话的 Map（只读）
export const conversationMapState = atom((get) => get(conversationMapAtom));

// 获取会话 ID 列表（只读）
export const conversationIdListState = atom((get) => get(conversationIdListAtom));

// 获取活跃会话 ID 集合（只读）
export const activeConversationIdsState = atom((get) => get(activeConversationIdsAtom));

// 获取视口会话 ID 集合（只读）
export const viewportConversationIdsState = atom((get) => get(viewportConversationIdsAtom));

// 获取会话摘要 Map（只读，用于排序和过滤）
export const conversationSummaryMapState = atom((get) => get(conversationSummaryMapAtom));

// ============================================================================
// 内部工具函数：从完整会话提取摘要
// ============================================================================

export const extractSummary = (conversation: ConversationType): ConversationSummary => ({
  conversationId: conversation.conversationId,
  talkId: conversation.talkId,
  mockExternal: conversation.mockExternal,
  storeId: conversation.storeId,
  lastMsgTime: conversation.lastMsgTime,
  remindMark: conversation.remindMark,
  controlStatus: conversation.controlStatus,
  supervisePower: conversation.supervisePower,
  sbsIndex: conversation.sbsIndex,
  todoType: conversation.todoType,
  colorAsterisk: conversation.colorAsterisk,
  lastAssignedId: conversation.lastAssignedId,
  salesStatus: conversation.salesStatus,
  unreadCount: conversation.unreadCount,
  processed: conversation.processed,
  isAffiliateSip: conversation.isAffiliateSip,
  botMark: conversation.botMark,
  isSplsNotClose: conversation.isSplsNotClose,
  isSplsBotReplied: conversation.isSplsBotReplied,
  isWaitAssign: conversation.isWaitAssign,
  inReception: conversation.inReception,
  isSplsManualClose: conversation.isSplsManualClose,
  channel: conversation.channel,
  region: conversation.region,
  tunnelAccountId: conversation.tunnelAccountId,
  isSellerAffiliateChat: conversation.isSellerAffiliateChat,
  existKnowledgePopup: conversation.existKnowledgePopup,
});

// ============================================================================
// 内部工具函数：合并会话数据
// ============================================================================

const mergeConversation = (
  prev: ConversationType | undefined,
  next: ConversationType,
): ConversationType => {
  if (!prev) {
    return next;
  }

  // 保留较新的 todoId 相关信息
  const todoInfo =
    (Number(prev.todoId) || 0) > (Number(next.todoId) || 0)
      ? {
          todoId: prev.todoId,
          todoType: prev.todoType,
          todoTypeName: prev.todoTypeName,
        }
      : {
          todoId: next.todoId,
          todoType: next.todoType,
          todoTypeName: next.todoTypeName,
        };

  // 保留 topBarInfo（如果新数据没有但旧数据有）
  if (prev.topBarInfo && !next.topBarInfo) {
    return {
      ...prev,
      ...next,
      ...todoInfo,
    };
  }

  return {
    ...prev,
    ...next,
    ...todoInfo,
  };
};

// ============================================================================
// CRUD Hooks
// ============================================================================

// 设置/更新单个会话
export const useSetConversation = () => {
  return useAtomCallback(
    useCallback((get, set, conversationId: ConversationId, conversation: ConversationType) => {
      const map = new Map(get(conversationMapAtom));
      const summaryMap = new Map(get(conversationSummaryMapAtom));
      const idList = get(conversationIdListAtom);
      const activeIds = get(activeConversationIdsAtom);
      const viewportIds = get(viewportConversationIdsAtom);

      console.log('[useSetConversation] conversationId', conversationId, conversation);

      // 合并保护集合：活跃会话 + 视口会话
      const protectedIds = new Set([...activeIds, ...viewportIds]);

      // 重新激活受保护的会话（将其移到 Map 末尾，防止被 LRU 淘汰）
      protectedIds.forEach((protectedId) => {
        const value = map.get(protectedId);
        if (value) {
          map.delete(protectedId);
          map.set(protectedId, value);
        }
      });

      // 合并会话数据
      const prev = map.get(conversationId);
      const merged = mergeConversation(prev, conversation);
      map.set(conversationId, merged);

      // 同步更新 summaryMap（不进行 LRU 回收）
      summaryMap.set(conversationId, extractSummary(merged));

      // LRU 淘汰：超过限制时删除最旧的（跳过受保护的会话）
      // 注意：只回收 conversationMapAtom，不回收 summaryMap
      while (map.size > maxConversationsLimit) {
        let keyToDelete: ConversationId | undefined;

        for (const key of map.keys()) {
          if (!protectedIds.has(key)) {
            keyToDelete = key;
            break;
          }
        }

        if (keyToDelete) {
          map.delete(keyToDelete);
          clearNimSessions();
        } else {
          break;
        }
      }

      set(conversationMapAtom, map);
      set(conversationSummaryMapAtom, summaryMap);

      // 如果是新会话，更新 ID 列表
      if (!idList.includes(conversationId)) {
        set(conversationIdListAtom, [...idList, conversationId]);
      }
    }, []),
  );
};

// 仅更新已存在的会话
export const useUpdateConversation = () => {
  return useAtomCallback(
    useCallback((get, set, conversationId: ConversationId, conversation: ConversationType) => {
      const map = get(conversationMapAtom);
      const summaryMap = get(conversationSummaryMapAtom);

      console.log('[useUpdateConversation] conversationId', conversationId, conversation);

      // 只要 summaryMap 中存在就更新（因为 map 可能被回收但 summary 保留）
      if (map.has(conversationId) || summaryMap.has(conversationId)) {
        const newMap = new Map(map);
        const newSummaryMap = new Map(summaryMap);

        const prev = newMap.get(conversationId);
        const merged = mergeConversation(prev, conversation);

        // 如果完整数据存在则更新
        if (map.has(conversationId)) {
          newMap.set(conversationId, merged);
          set(conversationMapAtom, newMap);
        }

        // 始终更新 summaryMap
        newSummaryMap.set(conversationId, extractSummary(merged));
        set(conversationSummaryMapAtom, newSummaryMap);
      }
    }, []),
  );
};

// 批量更新会话
export const useBatchUpdateConversations = () => {
  return useAtomCallback(
    useCallback((get, set, conversations: ConversationType[]) => {
      const map = new Map(get(conversationMapAtom));
      const summaryMap = new Map(get(conversationSummaryMapAtom));
      const idList = [...get(conversationIdListAtom)];
      const activeIds = get(activeConversationIdsAtom);
      const viewportIds = get(viewportConversationIdsAtom);

      // 合并保护集合：活跃会话 + 视口会话
      const protectedIds = new Set([...activeIds, ...viewportIds]);

      // 重新激活受保护的会话
      protectedIds.forEach((protectedId) => {
        const value = map.get(protectedId);
        if (value) {
          map.delete(protectedId);
          map.set(protectedId, value);
        }
      });

      conversations.forEach((conv) => {
        const prev = map.get(conv.conversationId);
        const merged = mergeConversation(prev, conv);
        map.set(conv.conversationId, merged);

        // 同步更新 summaryMap（不进行 LRU 回收）
        summaryMap.set(conv.conversationId, extractSummary(merged));

        if (!idList.includes(conv.conversationId)) {
          idList.push(conv.conversationId);
        }
      });

      // LRU 淘汰（跳过受保护的会话）
      // 注意：只回收 conversationMapAtom，不回收 summaryMap
      while (map.size > maxConversationsLimit) {
        let keyToDelete: ConversationId | undefined;

        for (const key of map.keys()) {
          if (!protectedIds.has(key)) {
            keyToDelete = key;
            break;
          }
        }

        if (keyToDelete) {
          map.delete(keyToDelete);
          clearNimSessions();
        } else {
          break;
        }
      }

      set(conversationMapAtom, map);
      set(conversationSummaryMapAtom, summaryMap);
      set(conversationIdListAtom, idList);
    }, []),
  );
};

// 删除会话
export const useDeleteConversation = () => {
  return useAtomCallback(
    useCallback((get, set, conversationId: ConversationId | ConversationId[]) => {
      const map = new Map(get(conversationMapAtom));
      const summaryMap = new Map(get(conversationSummaryMapAtom));
      const idList = get(conversationIdListAtom);
      const idsToDelete = Array.isArray(conversationId) ? conversationId : [conversationId];

      idsToDelete.forEach((id) => {
        if (map.delete(id)) {
          clearNimSessions();
          conversationAtomFamily.remove(id);
        }
        // 同时删除 summaryMap 中的数据
        summaryMap.delete(id);
      });

      const newIdList = idList.filter((id) => !idsToDelete.includes(id));

      set(conversationMapAtom, map);
      set(conversationSummaryMapAtom, summaryMap);
      set(conversationIdListAtom, newIdList);
    }, []),
  );
};

// 清空所有会话
export const useClearConversations = () => {
  return useAtomCallback(
    useCallback((get, set) => {
      const map = get(conversationMapAtom);

      // 清理所有 atomFamily 实例
      map.forEach((_, id) => {
        conversationAtomFamily.remove(id);
      });

      set(conversationMapAtom, new Map());
      set(conversationSummaryMapAtom, new Map());
      set(conversationIdListAtom, []);
      clearNimSessions();
      console.log('[useClearConversations] clear all conversations');
    }, []),
  );
};

// 获取单个会话（非响应式，用于回调中）
export const useGetConversationById = () => {
  return useAtomCallback(
    useCallback((get, _, conversationId: ConversationId) => {
      return get(conversationMapAtom).get(conversationId);
    }, []),
  );
};

// 响应式获取单个会话
export const useConversation = (conversationId: ConversationId) => {
  return useAtomValue(conversationAtomFamily(conversationId));
};

// ============================================================================
// 活跃会话管理
// ============================================================================

// 添加活跃会话
export const useAddActiveConversation = () => {
  return useAtomCallback(
    useCallback((get, set, conversationId: ConversationId) => {
      const activeIds = new Set(get(activeConversationIdsAtom));
      activeIds.add(conversationId);
      set(activeConversationIdsAtom, activeIds);
    }, []),
  );
};

// 移除活跃会话
export const useRemoveActiveConversation = () => {
  return useAtomCallback(
    useCallback((get, set, conversationId: ConversationId) => {
      const activeIds = new Set(get(activeConversationIdsAtom));
      activeIds.delete(conversationId);
      set(activeConversationIdsAtom, activeIds);
    }, []),
  );
};

// ============================================================================
// 视口会话管理（防止视口内会话被回收）
// ============================================================================

// 更新视口会话 ID 集合
export const useSetViewportConversationIds = () => {
  return useSetAtom(viewportConversationIdsAtom);
};

// 获取视口会话 ID 集合
export const useViewportConversationIds = () => {
  return useAtomValue(viewportConversationIdsAtom);
};

// ============================================================================
// 按需加载会话（用于被回收后重新获取）
// ============================================================================

// 检查会话是否正在加载中
export const isConversationLoading = (conversationId: ConversationId): boolean => {
  return loadingConversationIds.has(conversationId);
};

// 微任务批处理收集器（安全网：将同一微任务内的多个 resolve 合并为一次 batchUpdate）
let pendingBatch: ConversationType[] = [];
let flushScheduled = false;
let batchUpdateFn: ((convs: ConversationType[]) => void) | null = null;

function scheduleFlush() {
  if (!flushScheduled) {
    flushScheduled = true;
    queueMicrotask(() => {
      const batch = pendingBatch;
      pendingBatch = [];
      flushScheduled = false;
      if (batch.length > 0 && batchUpdateFn) {
        batchUpdateFn(batch);
      }
    });
  }
}

// 按需加载单个会话（带微任务批处理）
export const useFetchConversationOnDemand = () => {
  const batchUpdate = useBatchUpdateConversations();
  batchUpdateFn = batchUpdate;

  return useAtomCallback(
    useCallback(
      async (get, _, conversationId: ConversationId): Promise<ConversationType | null> => {
        // 已有数据，无需加载
        const existing = get(conversationMapAtom).get(conversationId);
        if (existing) return existing;

        // 正在加载中，避免重复请求
        if (loadingConversationIds.has(conversationId)) return null;

        try {
          loadingConversationIds.add(conversationId);
          const conversation = await batchGetTalk(conversationId);
          // 收集到 pendingBatch，通过微任务合并为一次 batchUpdateConversations
          pendingBatch.push(conversation);
          scheduleFlush();
          return conversation;
        } catch (error) {
          console.error('[lruConversation] Failed to fetch conversation:', conversationId, error);
          return null;
        } finally {
          loadingConversationIds.delete(conversationId);
        }
      },
      [],
    ),
  );
};

// 批量预加载会话（用于视口附近的预加载）
export const usePrefetchViewportConversations = () => {
  const batchUpdateConversations = useBatchUpdateConversations();

  return useAtomCallback(
    useCallback(
      async (get, _, conversationIds: ConversationId[]): Promise<void> => {
        const map = get(conversationMapAtom);

        // 过滤出需要加载的 ID（不在 Map 中且不在加载中）
        const missingIds = conversationIds.filter(
          (id) => !map.has(id) && !loadingConversationIds.has(id),
        );

        if (missingIds.length === 0) return;

        // 标记为加载中
        missingIds.forEach((id) => loadingConversationIds.add(id));

        try {
          // 使用 batchGetTalk 批量获取，它内部会自动合并请求
          const results = await Promise.allSettled(missingIds.map((id) => batchGetTalk(id)));

          const conversationsToUpdate: ConversationType[] = [];

          results.forEach((result) => {
            if (result.status === 'fulfilled') {
              conversationsToUpdate.push(result.value);
            }
          });

          if (conversationsToUpdate.length > 0) {
            batchUpdateConversations(conversationsToUpdate);
          }
        } finally {
          missingIds.forEach((id) => loadingConversationIds.delete(id));
        }
      },
      [batchUpdateConversations],
    ),
  );
};

// 检查会话是否存在于 Map 中
export const useHasConversation = () => {
  return useAtomCallback(
    useCallback((get, _, conversationId: ConversationId): boolean => {
      return get(conversationMapAtom).has(conversationId);
    }, []),
  );
};
